import type { QrScanErgebnis } from './qrScanner';

/**
 * QR-Codes im Browser lesen.
 *
 * ⚠️ **Die alte Begründung stimmte zur Hälfte.** Sie lautete: iOS-WebKit hat kein
 * `BarcodeDetector`, ein Ersatz wäre fremder Quelltext im Bündel (Prämisse des Nutzers). Das
 * Erste gilt weiter — auf dem **Gerät** bleibt es bei der nativen Bühne. Das Zweite gilt
 * nicht: `BarcodeDetector` ist eine **Browser-API**, kein Fremdcode. `zxing` oder `jsQR` wären
 * welcher; diese Schnittstelle ist keiner.
 *
 * Am 2026-09-22 in Chrome 153 gemessen: `getSupportedFormats()` führt `qr_code`.
 *
 * ⚠️ **Die Frist gehört dazu.** Sie war einer der zwei Gründe, weshalb die native Bühne
 * überhaupt selbst gebaut wurde (`qrScanner.ts`): `@capacitor/barcode-scanner` kennt kein
 * `stopScan`. Ein Rückfall ohne Frist nähme genau das wieder zurück.
 */

/** Was `BarcodeDetector` von sich preisgibt — die Schnittstelle fehlt in `lib.dom`. */
interface Erkenner {
  detect(quelle: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

interface ErkennerBauart {
  new (optionen: { formats: string[] }): Erkenner;
  getSupportedFormats(): Promise<string[]>;
}

const bauart = (): ErkennerBauart | null =>
  (globalThis as { BarcodeDetector?: ErkennerBauart }).BarcodeDetector ?? null;

/** Liest dieser Browser QR-Codes? */
export async function kannQrLesen(
  gib: () => ErkennerBauart | null = bauart,
): Promise<boolean> {
  const b = gib();
  if (!b) return false;
  try {
    return (await b.getSupportedFormats()).includes('qr_code');
  } catch {
    return false;
  }
}

/** Was der Scanner braucht — im Test Attrappen. */
export interface QrWebTeile {
  erkenner: () => Promise<Erkenner | null>;
  /** Der Kamerastrom. */
  strom: () => Promise<MediaStream | null>;
  /** Die Bühne; meldet Abbruch. */
  buehne: () => QrBuehne;
  /** Ein Bild aus dem Strom, auf das der Erkenner schauen kann. */
  bild: (strom: MediaStream | null) => CanvasImageSource | null;
  jetzt: () => number;
  /** Wie oft nachgesehen wird. */
  taktMs: number;
}

export interface QrBuehne {
  oeffne(optionen: { anleitung: string; strom: MediaStream | null; abbrechen: () => void }): void;
  schliesse(): void;
}

/**
 * Sucht, bis etwas gefunden ist, die Frist abläuft oder der Prüfer abbricht.
 *
 * Die drei Ausgänge sind dieselben wie nativ (`QrScanErgebnis`) — und **keiner davon ist ein
 * Fehler**: `scanner.ts` macht aus allen dreien `null`, ohne Meldung.
 */
export async function scanneImBrowser(
  optionen: { instructions?: string; timeoutSeconds?: number },
  teile: QrWebTeile,
): Promise<QrScanErgebnis> {
  const erkenner = await teile.erkenner();
  if (!erkenner) return { cancelled: true };

  const strom = await teile.strom();
  const buehne = teile.buehne();
  const frist = (optionen.timeoutSeconds ?? 0) * 1000;
  const start = teile.jetzt();

  let takt: ReturnType<typeof setInterval> | null = null;
  const halt = () => {
    if (takt !== null) clearInterval(takt);
    takt = null;
  };

  try {
    return await new Promise<QrScanErgebnis>((fertig) => {
      const beende = (ergebnis: QrScanErgebnis) => {
        halt();
        fertig(ergebnis);
      };

      buehne.oeffne({
        anleitung: optionen.instructions ?? '',
        strom,
        abbrechen: () => beende({ cancelled: true }),
      });

      let laeuftSuche = false;
      takt = setInterval(() => {
        // ⚠️ Zuerst die Frist, dann die Suche: ein iPad, das mit offener Kamera liegenbleibt,
        // soll auch dann zurückkommen, wenn die Erkennung gerade hängt.
        if (frist > 0 && teile.jetzt() - start >= frist) {
          beende({ timedOut: true });
          return;
        }
        // Ein Durchgang nach dem anderen — sonst stapeln sich die Erkennungen, sobald eine
        // länger braucht als der Takt.
        if (laeuftSuche) return;
        const quelle = teile.bild(strom);
        if (!quelle) return;
        laeuftSuche = true;
        void erkenner
          .detect(quelle)
          .then((treffer) => {
            const wert = treffer.find((t) => t.rawValue)?.rawValue;
            if (wert) beende({ value: wert });
          })
          .catch(() => undefined)
          .finally(() => {
            laeuftSuche = false;
          });
      }, teile.taktMs);
    });
  } finally {
    halt();
    buehne.schliesse();
    for (const spur of strom?.getTracks() ?? []) spur.stop();
  }
}

/* ------------------------------------------------------------------ Die echten Teile */

const KNOPF =
  'appearance:none;border:0;border-radius:10px;padding:10px 16px;font:600 15px system-ui;' +
  'background:#ffffff1a;color:#fff;cursor:pointer';

/**
 * Die Bühne aus reinem DOM — ein **quadratisches** Sichtfenster, wie nativ.
 *
 * Die Form war die zweite Vorgabe, an der `@capacitor/barcode-scanner` scheiterte; sie hier
 * fallen zu lassen hiesse, den Rückfall anders aussehen zu lassen als die Sache selbst.
 */
export function domQrBuehne(): QrBuehne & { videoElement: () => HTMLVideoElement | null } {
  let huelle: HTMLDivElement | null = null;
  let video: HTMLVideoElement | null = null;

  return {
    videoElement: () => video,
    oeffne({ anleitung, strom, abbrechen }) {
      huelle = document.createElement('div');
      // `pointer-events:auto`: ein offener Radix-Dialog setzt `none` auf `body` (2026-11-01).
      huelle.style.cssText =
        'position:fixed;inset:0;z-index:2000;background:#000;display:flex;pointer-events:auto;' +
        'flex-direction:column;align-items:center;justify-content:center;gap:16px';
      huelle.setAttribute('data-buehne', ''); // s. `ui/buehne.ts`

      const fenster = document.createElement('div');
      fenster.style.cssText =
        'width:min(70vw,70vh);aspect-ratio:1;overflow:hidden;border-radius:16px;' +
        'outline:3px solid #fff6;background:#111';

      video = document.createElement('video');
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.style.cssText = 'width:100%;height:100%;object-fit:cover';
      if (strom) video.srcObject = strom;
      fenster.append(video);

      const text = document.createElement('p');
      text.textContent = anleitung;
      text.style.cssText = 'color:#fff;font:500 16px system-ui;margin:0;text-align:center';

      const zurueck = document.createElement('button');
      zurueck.type = 'button';
      zurueck.style.cssText = KNOPF;
      zurueck.textContent = 'Abbrechen';
      zurueck.onclick = abbrechen;

      huelle.append(fenster, text, zurueck);
      document.body.append(huelle);
    },
    schliesse() {
      huelle?.remove();
      huelle = null;
      video = null;
    },
  };
}

export function baueQrTeile(ueber: Partial<QrWebTeile> = {}): QrWebTeile {
  // Eine Bühne für den ganzen Lauf, damit `bild` an dasselbe Element kommt wie `oeffne`.
  let gebaut: (QrBuehne & { videoElement: () => HTMLVideoElement | null }) | null = null;
  return {
    erkenner:
      ueber.erkenner ??
      (async () => {
        const b = bauart();
        if (!b || !(await kannQrLesen())) return null;
        return new b({ formats: ['qr_code'] });
      }),
    strom:
      ueber.strom ??
      (() =>
        navigator.mediaDevices.getUserMedia({
          // Die rückwärtige Kamera, wenn es eine gibt — am Rechner gibt es keine, dann nimmt
          // der Browser die einzige, die da ist.
          video: { facingMode: 'environment' },
        })),
    buehne:
      ueber.buehne ??
      (() => {
        gebaut = domQrBuehne();
        return gebaut;
      }),
    bild:
      ueber.bild ??
      (() => {
        const el = gebaut?.videoElement();
        // Ein Element ohne Masse liefert dem Erkenner nichts — dann lieber gar nicht fragen.
        return el && el.videoWidth > 0 ? el : null;
      }),
    jetzt: ueber.jetzt ?? (() => Date.now()),
    taktMs: ueber.taktMs ?? 200,
  };
}
